/**
 * PATH: packages/ui/src/components/settings/security-tab/SessionsSection.tsx
 * PURPOSE: Display and manage active login sessions (revoke individual / all).
 * WHY: Extracted from SecurityTab to keep each file under 300 lines.
 *
 * FLOW:
 *   ┌──────────────┐    ┌───────────────┐    ┌──────────────┐
 *   │ fetchSessions│───▶│ display list  │───▶│ revoke action│
 *   └──────────────┘    └───────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ../../lib/utils (cn): classname merge
 */

import { useState, useEffect } from 'react'
import { Monitor, Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { Session, SessionsSectionProps } from './security-tab-types'

export function SessionsSection({
  fetchSessions,
  revokeSession,
  revokeAllSessions,
}: SessionsSectionProps) {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    setLoadingSessions(true)
    setSessionError(null)
    fetchSessions()
      .then((results) => {
        if (!isMounted) return
        setSessions(Array.isArray(results) ? results : [])
      })
      .catch(() => {
        if (!isMounted) return
        setSessionError('Unable to load sessions')
      })
      .finally(() => {
        if (!isMounted) return
        setLoadingSessions(false)
      })
    return () => { isMounted = false }
  }, [fetchSessions])

  const handleRevokeAll = async () => {
    setLoadingSessions(true)
    setSessionError(null)
    try {
      const result = await revokeAllSessions()
      if (result?.success) {
        const refreshed = await fetchSessions()
        setSessions(Array.isArray(refreshed) ? refreshed : [])
      }
    } catch {
      setSessionError('Unable to revoke sessions')
    } finally {
      setLoadingSessions(false)
    }
  }

  const handleRevokeOne = async (sessionId: string) => {
    setLoadingSessions(true)
    setSessionError(null)
    try {
      const result = await revokeSession(sessionId)
      if (result?.success) {
        const refreshed = await fetchSessions()
        setSessions(Array.isArray(refreshed) ? refreshed : [])
      }
    } catch {
      setSessionError('Unable to revoke session')
    } finally {
      setLoadingSessions(false)
    }
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-muted">
            <Monitor className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Active Sessions</h3>
            <p className="text-sm text-muted-foreground">Manage devices where you're logged in</p>
          </div>
        </div>
        <button onClick={handleRevokeAll} className="text-sm text-destructive hover:text-destructive/80">
          Sign out all devices
        </button>
      </div>

      <div className="space-y-2">
        {loadingSessions && (
          <div className="flex items-center justify-center p-6 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading sessions...
          </div>
        )}

        {!loadingSessions && sessionError && (
          <div className="flex items-center justify-center p-6 text-destructive text-sm">{sessionError}</div>
        )}

        {!loadingSessions && !sessionError && sessions.length === 0 && (
          <div className="flex items-center justify-center p-8 text-muted-foreground text-sm">
            <p>No active sessions</p>
          </div>
        )}

        {!loadingSessions && !sessionError && sessions.map((session) => (
          <div
            key={session.id}
            className={cn(
              'flex items-center justify-between p-4 rounded-xl border border-border',
              session.isCurrent ? 'bg-muted/30' : 'bg-background',
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn('p-2 rounded-lg', session.isCurrent ? 'bg-green-500/10' : 'bg-muted')}>
                <Monitor className={cn('w-4 h-4', session.isCurrent ? 'text-green-500' : 'text-muted-foreground')} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{session.device || 'Device'}</p>
                  {session.isCurrent && (
                    <span className="px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-medium">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {session.browser || 'Browser'} • {session.ip || 'IP'} • {session.lastActive || 'Active now'}
                </p>
              </div>
            </div>
            {!session.isCurrent && (
              <button
                onClick={() => handleRevokeOne(session.id)}
                className="text-sm text-destructive hover:text-destructive/80"
              >
                Sign out
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
